import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Gift, Sparkles, PartyPopper, X } from "lucide-react";

/**
 * Trial Extension Notice Modal
 * يظهر مرة واحدة للمستخدم عند تمديد تجربته إدارياً.
 * بعد ضغطه "حسناً" نطلب API لوضع seen=true.
 */
export default function TrialExtensionNotice() {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const notice = user?.trial_extension_notice;
  const API = `${window.location.origin}/api`;

  useEffect(() => {
    if (notice && notice.seen === false) {
      const t = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, [notice]);

  const dismiss = async () => {
    setOpen(false);
    try {
      await axios.post(`${API}/admin/trial/dismiss-notice`);
      if (refreshUser) refreshUser();
    } catch {}
  };

  if (!open || !notice) return null;

  const days = notice.extra_days || 5;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300"
      data-testid="trial-extension-notice-modal"
    >
      <Card className="w-full max-w-md bg-gradient-to-br from-slate-900 via-emerald-950/50 to-slate-900 border-2 border-emerald-500/50 shadow-2xl overflow-hidden">
        {/* Celebration banner */}
        <div className="relative h-3 bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-500 animate-pulse" />

        <CardContent className="p-7 relative text-center">
          <button
            onClick={dismiss}
            className="absolute top-3 end-3 text-slate-500 hover:text-white transition"
            aria-label="إغلاق"
            data-testid="trial-extension-close-btn"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Icon */}
          <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-2xl shadow-emerald-500/40 relative">
            <Gift className="w-12 h-12 text-white" />
            <Sparkles className="w-5 h-5 text-yellow-300 absolute -top-1 -end-1 animate-pulse" />
            <Sparkles className="w-4 h-4 text-yellow-200 absolute bottom-0 start-0 animate-pulse" style={{ animationDelay: "0.3s" }} />
          </div>

          <div className="inline-flex items-center gap-2 bg-amber-400/20 text-amber-200 px-3 py-1 rounded-full text-xs font-bold mb-3">
            <PartyPopper className="w-3.5 h-3.5" />
            هدية خاصة لك
          </div>

          <h2 className="text-2xl font-extrabold text-white mb-2" data-testid="trial-extension-headline">
            تم تمديد فترتك التجريبية!
          </h2>

          <p className="text-slate-300 text-base mb-4">
            أهدتك إدارة التطبيق{" "}
            <span className="text-emerald-300 font-extrabold text-xl" dir="ltr">{days}</span>{" "}
            {days === 1 ? "يوم إضافي" : "أيام إضافية"} لتجربة كل المزايا
          </p>

          {/* سبب التمديد (إذا موجود) */}
          {notice.reason && notice.reason !== "تمديد إداري" && (
            <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 mb-4 text-sm text-slate-300">
              📩 {notice.reason}
            </div>
          )}

          {/* مزايا */}
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-5 text-start">
            <p className="text-emerald-300 font-bold text-sm mb-2 text-center">استمر في استخدام كامل المزايا:</p>
            <ul className="text-slate-300 text-sm space-y-1.5">
              <li className="flex items-center gap-2">✅ كل المبيعات والمخزون والتقارير</li>
              <li className="flex items-center gap-2">✅ الوضع الأوفلاين الكامل</li>
              <li className="flex items-center gap-2">✅ الفواتير المعلقة والتذكيرات</li>
              <li className="flex items-center gap-2">✅ واتساب وكشف الحساب</li>
            </ul>
          </div>

          <Button
            onClick={dismiss}
            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white py-6 text-base font-bold shadow-lg shadow-emerald-500/30"
            data-testid="trial-extension-accept-btn"
          >
            شكراً، استمر في الاستخدام 🚀
          </Button>

          <p className="text-slate-500 text-xs mt-4">
            استمتع بتجربة {days} أيام إضافية، وعندما تقرّر الاشتراك اضغط على تفعيل
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
